import { RefreshCw, SlidersHorizontal } from 'lucide-react';
import _ from 'lodash';
import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Actions as CONSUMPTION_ACTIONS } from '../../store/actions/consumptionActions';
import { Actions as REPORT_ACTIONS } from '../../store/actions/reportActions';
import { PeriodMaker } from '../utils/GlobalFunctions';
import GreenhouseEmissionsAndIntensityRatioRow from './GreenhouseEmissionsAndIntensityRatioRow';
import TotalGreenhouseEmissionsByScopeChart from './TotalGreenhouseEmissionsByScopeChart';
import EmissionsBySourceChart from './EmissionsBySourceChart';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { newSelect } from '../ui/newSelect';
import { Table, TableHeader, TableRow, TableCell } from '../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const GreenhouseEmissionsAndIntensityRatios = ({
  report,
  totalEmissionsByScope,
  consumptionFilter,
  metrics,
  organisationMetrics,
  organisationDetails,
  toggleConsumptionFilter,
  withMarketFactors,
  industries,
  orgOptions,
  currentOrganisation,
  getConsumption,
  saveIncludedOrgs,
  adminOrgs,
  includedOrganisations,
  orgsInReport,
  branding,
  hasAccess,
  metricValues,
}) => {
  const year = report.year;
  const [organisations, setOrganisations] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const selectedOrgs = localStorage.getItem('selectedOrgs');
    if (selectedOrgs) {
      setOrganisations(JSON.parse(selectedOrgs));
    }
    if (includedOrganisations?.length > 0) {
      toggleConsumptionFilter('m1');
      toggleConsumptionFilter('m2');
      toggleConsumptionFilter('m3');
    }
  }, []);

  useEffect(() => {
    const metricValuesThisYear = metricValues?.find((metrics) => metrics.year === String(year));
    const metricValuesPrevYear = metricValues?.find((metrics) => metrics.year === String(year - 1));
    if (!metricValuesThisYear && !metricValuesPrevYear) {
      toggleConsumptionFilter('revenue');
      toggleConsumptionFilter('m1');
      toggleConsumptionFilter('m2');
      toggleConsumptionFilter('m3');
      toggleConsumptionFilter('m4');
    }
  }, []);

  if (!report || !organisationDetails || !organisationMetrics || !totalEmissionsByScope) {
    return null;
  }

  const hasComparative = totalEmissionsByScope && totalEmissionsByScope[year - 1];
  const referenceYear = +report.endDate.split('-')[0];
  const lastPeriod = hasComparative ? PeriodMaker(report.startDate, report.endDate, referenceYear - 1) : null;
  const currentPeriod = PeriodMaker(report.startDate, report.endDate, referenceYear);
  const options = ['m1', 'm2', 'm3', 'revenue', 'co2e'];
  const filterClasses = consumptionFilter.map((item) => `hide-${item}`);

  return (
    <Card className='bg-card text-card-foreground my-8'>
      <CardHeader className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2'>
        <CardTitle className='text-lg font-semibold'>Total Reported Greenhouse Gas Emissions and Intensity Ratios</CardTitle>
        <div className='flex flex-col md:flex-row gap-2 items-start md:items-center'>
          {orgOptions?.length > 0 && hasAccess && (
            <>
              <div className='min-w-[230px]'>
                <newSelect
                  options={orgOptions}
                  value={organisations}
                  onValueChange={(selected) => {
                    if (!selected || selected.length === 0) {
                      setOrganisations([]);
                      localStorage.removeItem('selectedOrgs');
                    } else {
                      setOrganisations(selected);
                      localStorage.setItem('selectedOrgs', JSON.stringify(selected));
                    }
                  }}
                  placeholder='Child Organisations'
                  multiple={true}
                  clearable={true}
                  className='w-full'
                />
              </div>
              <Button
                id='Report-Generate-Button'
                className='bg-primary text-primary-foreground hover:bg-primary/90'
                onClick={() => {
                  if (organisations.length > 0) {
                    //call consumptionHelper
                    getConsumption(false, false, [...organisations?.map((org) => org.value)]);
                    saveIncludedOrgs([...organisations?.map((org) => org.value)]);
                  } else {
                    getConsumption(false, false);
                    saveIncludedOrgs([]);
                  }
                }}>
                Generate
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className='bg-secondary text-secondary-foreground hover:bg-secondary/80'
                      onClick={() => {
                        if (organisations.length > 0) {
                          getConsumption(false, false, [...organisations?.map((org) => org.value)]);
                          saveIncludedOrgs([...organisations?.map((org) => org.value)]);
                        } else {
                          getConsumption(false, false);
                          saveIncludedOrgs([]);
                        }
                      }}>
                      <RefreshCw size={16} strokeWidth={1.5} className='mr-2' />
                      Update
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className='text-xs'>
                      Organisations listed as unavailable were not owned by the parent organisation during the reporting
                      period. You can update the dates of ownership on the organisation page.
                    </span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}
          <Button
            id='filter-btn-mobile'
            className='bg-secondary text-secondary-foreground hover:bg-secondary/80 md:hidden'
            onClick={() => setShowFilters(true)}>
            <SlidersHorizontal size={16} strokeWidth={1.5} className='mr-2' />Filters
          </Button>
        </div>
      </CardHeader>
      <CardContent>
      <style>
        {`.hide-comparative .toggle-comparative { display: none }`}
        {`.hide-market .toggle-market { display: none }`}
        {`.hide-location .toggle-location { display: none }`}
        {options && options.map((key) => `.hide-${key} .toggle-${key} { display: none }`)}
      </style>
        {/* Desktop Filters */}
        <div className='md:flex flex-row flex-wrap gap-2 mb-4'>
          {withMarketFactors && (
            <>
              <Button
                id='market-based-btn'
                variant={consumptionFilter.includes('market') ? 'outline' : 'default'}
                onClick={() => {
                  toggleConsumptionFilter('market');
                  toggleConsumptionFilter('location');
                }}>
                Market Based
              </Button>
              <Button
                id='location-based-btn'
                variant={consumptionFilter.includes('market') ? 'default' : 'outline'}
                onClick={() => {
                  toggleConsumptionFilter('market');
                  toggleConsumptionFilter('location');
                }}>
                Location Based
              </Button>
            </>
          )}
          <Button
            id='co2e-btn'
            variant={consumptionFilter.includes('co2e') ? 'outline' : 'default'}
            onClick={() => toggleConsumptionFilter('co2e')}>
            Co2e
          </Button>
          <Button
            id='revenue-btn'
            variant={consumptionFilter.includes('revenue') ? 'outline' : 'default'}
            onClick={() => toggleConsumptionFilter('revenue')}>
            per million Revenue
          </Button>
          {organisationDetails &&
            ['intensityRatioMetric1ID', 'intensityRatioMetric2ID', 'intensityRatioMetric3ID'].map((ir, index) => {
              const orgIndustry = _.find(industries, { id: organisationDetails?.industry });
              const metricID = orgIndustry?.[ir];
              const metric = metrics && metrics.find((metric) => metric.id === metricID);
              const metricName =
                metric && (metric.name === 'Unit' ? orgIndustry.descriptionOfNonStandardRatio : metric.name);
              return (
                metric &&
                metricID &&
                metricID !== '230393' && (
                  <Button
                    key={metricID || index}
                    id={`metric-btn-${metricID}`}
                    variant={consumptionFilter.includes(`m${index + 1}`) ? 'outline' : 'default'}
                    onClick={() => toggleConsumptionFilter(`m${index + 1}`)}>
                    per {metricName}
                  </Button>
                )
              );
            })}
        </div>
        {/* Mobile Filter Modal */}
        {showFilters && (
          <div className='fixed inset-0 z-50 flex items-end justify-center md:hidden'>
            <div className='absolute inset-0 bg-black/40' onClick={() => setShowFilters(false)} />
            <div className='relative w-full bg-card text-card-foreground rounded-t-xl shadow-lg p-6 max-h-[80vh] overflow-y-auto'>
              <div className='flex flex-col gap-4'>
                {withMarketFactors && (
                  <div className='flex flex-row justify-start items-center gap-3 mb-3'>
                    <span>Show: </span>
                    <div
                      id='showLocationOrMarketBased-btns'
                      className='flex flex-row justify-start items-center flex-wrap gap-2.5'>
                      <Button
                        id='market-based-btn-mobile'
                        variant={consumptionFilter.includes('market') ? 'outline' : 'default'}
                        onClick={() => {
                          toggleConsumptionFilter('market');
                          toggleConsumptionFilter('location');
                        }}>
                        Market Based
                      </Button>
                      <Button
                        id='location-based-btn-mobile'
                        variant={consumptionFilter.includes('market') ? 'default' : 'outline'}
                        onClick={() => {
                          toggleConsumptionFilter('market');
                          toggleConsumptionFilter('location');
                        }}>
                        Location Based
                      </Button>
                    </div>
                  </div>
                )}
                <div className='flex flex-row items-center gap-2.5'>
                  <span>Show: </span>
                  <div id='showReportMetrics-btns' className='gap-2.5 flex mt-3 flex-wrap justify-start items-center'>
                    <Button
                      id='co2e-btn-mobile'
                      variant={consumptionFilter.includes('co2e') ? 'outline' : 'default'}
                      onClick={() => toggleConsumptionFilter('co2e')}>
                      Co2e
                    </Button>
                    <Button
                      id='revenue-btn-mobile'
                      variant={consumptionFilter.includes('revenue') ? 'outline' : 'default'}
                      onClick={() => toggleConsumptionFilter('revenue')}>
                      per million Revenue
                    </Button>
                    {organisationDetails &&
                      ['intensityRatioMetric1ID', 'intensityRatioMetric2ID', 'intensityRatioMetric3ID'].map((ir, index) => {
                        const orgIndustry = _.find(industries, { id: organisationDetails?.industry });
                        const metricID = orgIndustry?.[ir];
                        const metric = metrics && metrics.find((metric) => metric.id === metricID);
                        const metricName =
                          metric && (metric.name === 'Unit' ? orgIndustry.descriptionOfNonStandardRatio : metric.name);
                        return (
                          metric &&
                          metricID &&
                          metricID !== '230393' && (
                            <Button
                              key={metricID || index}
                              id={`metric-btn-mobile-${metricID}`}
                              variant={consumptionFilter.includes(`m${index + 1}`) ? 'outline' : 'default'}
                              onClick={() => toggleConsumptionFilter(`m${index + 1}`)}>
                              per {metricName}
                            </Button>
                          )
                        );
                      })}
                  </div>
                </div>
                <Button onClick={() => setShowFilters(false)} className='bg-muted text-muted-foreground w-full'>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className='mt-6'>
          {includedOrganisations?.length === 0 && (
            <>
              <div className='flex justify-center font-semibold text-[15px] border-b border-border mb-2'>
                {'Organisation: ' + (adminOrgs?.find((org) => org.id === currentOrganisation)?.name || '')}
              </div>
              <div className='overflow-x-auto'>
                <Table className={`${filterClasses.join(' ')} w-full text-right`}>
                  <TableHeader className='bg-muted'>
                    <TableRow>
                      <TableCell className='font-semibold text-left text-muted-foreground'>Scope</TableCell>
                      {hasComparative && (
                        <TableCell className='toggle-comparative toggle-co2e font-semibold text-muted-foreground'>
                          Total <span className='whitespace-nowrap'>(tCO2e)</span>
                          <br />({lastPeriod})
                        </TableCell>
                      )}
                      <TableCell className='toggle-co2e font-semibold text-muted-foreground'>
                        Total <span className='whitespace-nowrap'>(tCO2e)</span>
                        <br />({currentPeriod})
                      </TableCell>
                      {hasComparative && (
                        <TableCell className='toggle-comparative toggle-revenue font-semibold text-muted-foreground'>
                          tCO2e / <span className='whitespace-nowrap'>Revenue</span>
                          <br />({lastPeriod})
                        </TableCell>
                      )}
                      <TableCell className='toggle-revenue font-semibold text-muted-foreground'>
                        tCO2e / <span className='whitespace-nowrap'>Revenue</span>
                        <br />({currentPeriod})
                      </TableCell>
                      {organisationDetails &&
                        ['intensityRatioMetric1ID', 'intensityRatioMetric2ID', 'intensityRatioMetric3ID'].map(
                          (ir, index) => {
                            const orgIndustry = _.find(industries, { id: organisationDetails?.industry });
                            const metricID = orgIndustry?.[ir];
                            const metric = metrics && metrics.find((metric) => metric.id === metricID);
                            const metricName =
                              metric && (metric.name === 'Unit' ? orgIndustry.descriptionOfNonStandardRatio : metric.name);
                            return (
                              metric &&
                              metricID &&
                              metricID !== '230393' && [
                                hasComparative && (
                                  <TableCell
                                    key={`comp-m${index + 1}`}
                                    className={`toggle-m${index + 1} toggle-comparative font-semibold text-muted-foreground`}>
                                    tCO2e / <span className='whitespace-nowrap'>{metricName}</span>
                                    <br />({lastPeriod})
                                  </TableCell>
                                ),
                                <TableCell key={`curr-m${index + 1}`} className={`toggle-m${index + 1} font-semibold text-muted-foreground`}>
                                  tCO2e / <span className='whitespace-nowrap'>{metricName}</span>
                                  <br />({currentPeriod})
                                </TableCell>,
                              ]
                            );
                          },
                        )}
                    </TableRow>
                  </TableHeader>
                  <tbody>
                    <GreenhouseEmissionsAndIntensityRatioRow org={currentOrganisation} scope='1' key={1} />
                    <GreenhouseEmissionsAndIntensityRatioRow org={currentOrganisation} scope='2' key={2} />
                    <GreenhouseEmissionsAndIntensityRatioRow org={currentOrganisation} scope='3' key={3} />
                    <GreenhouseEmissionsAndIntensityRatioRow org={currentOrganisation} key={'All Scopes'} />
                  </tbody>
                </Table>
              </div>
            </>
          )}
          {totalEmissionsByScope[year] && includedOrganisations?.length > 0 && (
            <>
              <div className='flex justify-center font-semibold text-[15px] border-b border-border mb-2'>
                {'Organisations: ' + orgsInReport}
              </div>
              <div className='overflow-x-auto'>
                <Table className={`${filterClasses.join(' ')} w-full text-right`}>
                  <TableHeader>
                    <TableRow>
                      <TableCell className='font-semibold text-left'>Scope</TableCell>
                      {hasComparative && (
                        <TableCell className='toggle-comparative toggle-co2e font-semibold'>
                          Total <span className='whitespace-nowrap'>(tCO2e)</span>
                          <br />({lastPeriod})
                        </TableCell>
                      )}
                      <TableCell className='toggle-co2e font-semibold'>
                        Total <span className='whitespace-nowrap'>(tCO2e)</span>
                        <br />({currentPeriod})
                      </TableCell>
                      {hasComparative && (
                        <TableCell className='toggle-comparative toggle-revenue font-semibold'>
                          tCO2e / <span className='whitespace-nowrap'>Revenue</span>
                          <br />({lastPeriod})
                        </TableCell>
                      )}
                      <TableCell className='toggle-revenue font-semibold'>
                        tCO2e / <span className='whitespace-nowrap'>Revenue</span>
                        <br />({currentPeriod})
                      </TableCell>
                      {/* Uncomment and adapt if you want to show metrics for group/orgs */}
                      {/* {organisationDetails &&
                        ['intensityRatioMetric1ID', 'intensityRatioMetric2ID', 'intensityRatioMetric3ID'].map((ir, index) => {
                          const orgIndustry = _.find(industries, { id: organisationDetails?.industry });
                          const metricID = orgIndustry?.[ir];
                          const metric = metrics && metrics.find((metric) => metric.id === metricID);
                          const metricName =
                            metric && (metric.name === 'Unit' ? orgIndustry.descriptionOfNonStandardRatio : metric.name);
                          return (
                            metric &&
                            metricID &&
                            metricID !== '230393' && [
                              <TableCell key={`curr-m${index + 1}`} className={`toggle-m${index + 1} font-semibold`}>
                                tCO2e / <span className='whitespace-nowrap'>{metricName}</span><br/>({currentPeriod})
                              </TableCell>,
                              hasComparative && (
                                <TableCell key={`comp-m${index + 1}`} className={`toggle-m${index + 1} toggle-comparative font-semibold`}>
                                  tCO2e / <span className='whitespace-nowrap'>{metricName}</span><br/>({lastPeriod})
                                </TableCell>
                              ),
                            ]
                          );
                        })} */}
                    </TableRow>
                  </TableHeader>
                  <tbody>
                    <GreenhouseEmissionsAndIntensityRatioRow org={'all'} scope='1' key={1} />
                    <GreenhouseEmissionsAndIntensityRatioRow org={'all'} scope='2' key={2} />
                    <GreenhouseEmissionsAndIntensityRatioRow org={'all'} scope='3' key={3} />
                    <GreenhouseEmissionsAndIntensityRatioRow org={'all'} key={'All Scopes'} />
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
        <p className='mt-4'>tCO2e – tonnes of carbon dioxide equivalent emissions</p>
        <div className='flex flex-col md:flex-row w-full justify-evenly mt-8 gap-8'>
          <div className='bar-chart w-full md:w-1/2'>
            <TotalGreenhouseEmissionsByScopeChart />
          </div>
          <div className='donut-chart w-full md:w-1/2'>
            <EmissionsBySourceChart />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const mapStateToProps = (state) => {
  const { totalEmissionsByScope, withMarketFactors, years, filter } = state.consumption || {};
  const adminOrgs = state.admin?.organisations;
  const { cyfAdmin, whiteLabelAdmin } = state.profile?.details || {};
  const adminUser = cyfAdmin || whiteLabelAdmin || false;
  const { organisation, report } = state;
  const organisationList = organisation?.list;
  const organisations = adminUser ? adminOrgs : organisationList;
  const organisationMetrics = organisation?.metrics;
  const currentOrganisation = state.currentOrganisation;
  const isParent =
    organisation?.details?.parentOrganisation === true ||
    organisation?.details?.parentOrganisation === currentOrganisation ||
    organisation?.details?.parentOrganisation === null;
  const includedOrganisations = state.report?.includedOrgs;
  //Returns the Ids of the organisation Children
  const orgChildren = organisation?.hierarchy?.map((org) => {
    return org.org;
  });
  //Returns the organisation object of the children which the user has access to
  const accessibleChildren = adminUser
    ? _.uniq(
        orgChildren
          ?.map((org) => {
            const found = adminOrgs?.find((organisation) => organisation?.id === org);
            if (found) {
              return found;
            }
          })
          ?.filter(Boolean),
      )
    : _.uniq(
        orgChildren
          ?.map((org) => {
            const found = organisation?.list?.find((organisation) => organisation?.id === org);
            if (found) {
              return found;
            }
          })
          ?.filter(Boolean),
      );

  const currentOrgName = organisations?.find((org) => org.id === currentOrganisation)?.name;
  const orgsInReport =
    includedOrganisations?.length > 2
      ? currentOrgName + ' and ' + includedOrganisations?.length + ' others'
      : currentOrgName +
        ', ' +
        includedOrganisations?.map((organisation) => organisations?.find((org) => org.id === organisation)?.name).join(', ');

  let orgOptions = accessibleChildren?.map((org) => {
    // Check if organisation is owned during this period
    const ownership = organisation.hierarchy?.find(
      (o) =>
        o.org === org?.id &&
        (moment(report.details.startDate).isBetween(o.startDate, o.endDate, null, '[]') ||
          moment(report.details.endDate).isBetween(o.startDate, o.endDate, null, '[]') ||
          (moment(o.startDate).isBetween(report.details.startDate, report.details.endDate, null, '[]') &&
            moment(o.endDate).isBetween(report.details.startDate, report.details.endDate, null, '[]'))),
    );
    if (!ownership) {
      return { value: org?.id, label: org?.name, isDisabled: true };
    } else {
      return { value: org?.id, label: org?.name };
    }
  });

  const enabledOptions = orgOptions.filter((option) => !option.isDisabled);
  const disabledOptions = orgOptions.filter((option) => option.isDisabled);

  orgOptions = [
    {
      label: 'Available Organisations',
      options: enabledOptions,
    },
    {
      label: 'Unavailable Organisations',
      options: disabledOptions,
    },
  ];

  const features = state?.features;
  const availableFeatures = state.organisation?.details?.features;
  const feature = features?.find((feature) => feature.featureKey === 'ORGANISATION_HIERARCHY');
  const hasAccess = availableFeatures?.includes('ORGANISATION_HIERARCHY') || feature?.enabledByDefault;

  return {
    withMarketFactors,
    report: report?.details,
    totalEmissionsByScope: totalEmissionsByScope,
    years: years || [],
    metrics: state.metric?.list,
    organisationDetails: organisation?.details,
    organisationMetrics: organisationMetrics,
    consumptionFilter: filter || [],
    industries: organisation?.industries,
    metricValues: organisation?.metrics ? organisation.metrics : null,
    orgOptions: orgOptions, //accessibleChildren && accessibleChildren?.map((org) => ({ value: org?.id, label: org?.name })),
    currentOrganisation,
    adminOrgs: adminUser ? adminOrgs : organisation?.list,
    includedOrganisations,
    orgsInReport,
    branding: state.branding,
    hasAccess: hasAccess || false,
  };
};

const mapDispatchToProps = (dispatch) => ({
  toggleConsumptionFilter: (filter) => dispatch(CONSUMPTION_ACTIONS.toggleConsumptionFilter(filter)),
  getConsumption: (monthly, stepFunctionSucceeded, childOrgs) =>
    dispatch(CONSUMPTION_ACTIONS.getConsumption(monthly, stepFunctionSucceeded, childOrgs)),
  saveIncludedOrgs: (orgs) => dispatch(REPORT_ACTIONS.saveIncludedOrgs(orgs)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GreenhouseEmissionsAndIntensityRatios);
